import { useState, useMemo } from "react";
import { Link2, X, Check } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface OrphanRecordingResolverProps {
  recording: {
    id: string;
    closer_id: string;
    recorded_at: string;
    title: string | null;
  };
  onResolved: () => void;
}

interface CandidateDemo {
  id: string;
  prospect_name: string;
  demo_date: string;
  status: string | null;
  client_id: string;
}

interface ClientOption {
  id: string;
  company_name: string;
}

export function OrphanRecordingResolver({ recording, onResolved }: OrphanRecordingResolverProps) {
  const [expanded, setExpanded] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState("");
  const [linkingToClient, setLinkingToClient] = useState(false);

  // Fetch candidate demos when expanded
  const { data: candidates, isLoading: loadingCandidates } = useQuery({
    queryKey: ["orphan-candidates", recording.id],
    queryFn: async () => {
      const recordedDate = new Date(recording.recorded_at);
      const from = new Date(recordedDate);
      from.setDate(from.getDate() - 7);
      const to = new Date(recordedDate);
      to.setDate(to.getDate() + 7);

      const { data, error } = await supabase
        .from("demos")
        .select("id, prospect_name, demo_date, status, client_id")
        .eq("closer_id", recording.closer_id)
        .gte("demo_date", format(from, "yyyy-MM-dd"))
        .lte("demo_date", format(to, "yyyy-MM-dd"))
        .order("demo_date", { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data ?? []) as CandidateDemo[];
    },
    enabled: expanded,
  });

  // Fetch all clients for client-only fallback (D-01, D-03) when expanded
  const { data: allClients } = useQuery({
    queryKey: ["orphan-resolver-clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, company_name")
        .order("company_name");
      if (error) throw error;
      return (data ?? []) as ClientOption[];
    },
    enabled: expanded,
  });

  const filteredClients = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    const list = allClients ?? [];
    if (!q) return list;
    return list.filter((c) => (c.company_name ?? "").toLowerCase().includes(q));
  }, [allClients, clientFilter]);

  const handleLink = async (demo: CandidateDemo) => {
    setLinking(demo.id);
    try {
      const { error } = await supabase.functions.invoke("resolve-recording", {
        body: {
          recording_id: recording.id,
          action: "link",
          demo_id: demo.id,
          client_id: demo.client_id,
        },
      });
      if (error) throw error;
      toast.success("Recording linked to demo");
      onResolved();
    } catch (err: unknown) {
      toast.error("Failed to link recording");
    } finally {
      setLinking(null);
    }
  };

  const handleLinkToClient = async (clientId: string) => {
    setLinkingToClient(true);
    try {
      const { error } = await supabase.functions.invoke("resolve-recording", {
        body: {
          recording_id: recording.id,
          action: "link",
          demo_id: null,
          client_id: clientId,
        },
      });
      if (error) throw error;
      toast.success("Recording linked to client");
      onResolved();
    } catch (err: unknown) {
      toast.error("Failed to link recording to client");
    } finally {
      setLinkingToClient(false);
    }
  };

  const handleDismiss = async () => {
    // D-04/D-15: UI-only collapse. No mutation fired. Orphan stays but admin has reviewed it.
    setExpanded(false);
    toast.info("Recording dismissed -- still unlinked but reviewed");
  };

  if (!expanded) {
    return (
      <div className="flex items-center gap-2">
        <BadgeVenture variant="warning">Orphan</BadgeVenture>
        <Button variant="ghost" size="sm" onClick={() => setExpanded(true)} title="Link to Demo">
          <Link2 className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2 min-w-[240px]">
      <div className="flex items-center justify-between">
        <BadgeVenture variant="warning">Orphan</BadgeVenture>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={handleDismiss} title="Dismiss">
            Dismiss
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(false)} title="Cancel">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loadingCandidates ? (
        <div className="h-8 bg-muted animate-pulse rounded" />
      ) : !candidates || candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No matching demos found for this closer in the past week. You may need to create the demo record first.
        </p>
      ) : (
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {candidates.map((demo) => (
            <div
              key={demo.id}
              className="flex items-center justify-between gap-2 text-xs p-1.5 rounded hover:bg-surface"
            >
              <span className="truncate">
                {demo.prospect_name} -- {format(new Date(demo.demo_date), "MMM d")} ({demo.status ?? "unknown"})
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                disabled={linking === demo.id}
                onClick={() => handleLink(demo)}
                title="Link this demo"
              >
                <Check className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* D-01, D-02, D-03: Client-only fallback — always rendered when expanded, even if candidates exist. */}
      <div className="mt-4 pt-4 border-t border-border">
        <h4 className="text-sm font-medium mb-2">No matching demo? Link to client instead</h4>
        <p className="text-xs text-muted-foreground mb-3">
          Link this recording directly to a client without a demo row.
        </p>
        <div className="flex flex-col gap-2">
          <Input
            placeholder="Search clients…"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            disabled={linkingToClient}
          />
          <Select onValueChange={handleLinkToClient} disabled={linkingToClient}>
            <SelectTrigger>
              <SelectValue placeholder="Select a client to link…" />
            </SelectTrigger>
            <SelectContent>
              {filteredClients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Test hook: exposes filtered clients list and per-client pick handlers so tests can drive the flow without Radix portal quirks in jsdom. No visible UI. */}
          <div data-testid="filtered-clients-debug" className="sr-only">
            {filteredClients.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`pick-client-${c.id}`}
                onClick={() => handleLinkToClient(c.id)}
              >
                {c.company_name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
